import {AlignmentType} from "../components/JourneyMapEditor/CellEditor/EditorCommon";
import {BehaviorLines} from "./CellContent/BehaviorLines";
import {CellContentInterface} from "./CellContent/CellContentInterface";
import {PerceptionContent} from "./CellContent/PerceptionContent";
import {TextStyle} from "./CellContent/TextStyle";
import {debug_log} from "../utils/debug";

export type EditorType = 'perception' | 'behavior' | 'action';

export function isEditorType(editor_type: any): editor_type is EditorType {
    return editor_type === 'perception'
        || editor_type === 'behavior'
        || editor_type === 'action';
}


export class CellContent {
    static readonly __CLASS__ = 'CellContent';
    static readonly INITIAL_TEXT = '';

    private editor_type: EditorType | null = null;
    private content: CellContentInterface | null = null;

    constructor(editor_type: EditorType | null, content?: CellContentInterface | null) {
        this.editor_type = editor_type;
        this.content = content ?? null;
    }

    get IsInitialState(): boolean {
        return this.editor_type === null;
    }

    usePerceptionContent(): [PerceptionContent, (perception_content: PerceptionContent) => CellContent] {
        const perception_content = this.editor_type === null
            ? PerceptionContent.createNew()
            : this.content;

        if (!(perception_content instanceof PerceptionContent)) {
            debug_log("CellContent.useEditorState", this);
            throw new Error("not PerceptionContent");
        }

        return [
            perception_content,
            (perception_content: PerceptionContent) => {
                return new CellContent('perception', perception_content);
            }
        ];
    }

    useBehaviorLines(): [BehaviorLines, (behavior_lines: BehaviorLines) => CellContent] {
        const behavior_lines = (this.editor_type === null)
            ? BehaviorLines.fromStr(CellContent.INITIAL_TEXT)
            : this.content;

        if (!(behavior_lines instanceof BehaviorLines)) {
            debug_log("CellContent.useBehaviorLines", this);
            throw new Error("not BehaviorLines");
        }

        return [
            behavior_lines,
            (behavior_lines: BehaviorLines) => {
                return new CellContent('behavior', behavior_lines);
            }
        ];
    }

    compatibleEditorType(other: CellContent): boolean {
        // action と behavior を区別してないから、 画像情報が action にもついてしまう という問題がある
        return this.editor_type === other.editor_type;
    }

    equals(other: CellContent) {
        if (this.editor_type !== other.editor_type) {
            return false;
        }

        if (this.content === null || other.content === null) {
            return false;
        }

        return this.content.equals(other.content);
    }

    toJSON() {
        return {
            editor_type: this.editor_type,
            content: this.content === null ? null : this.content.toJSON(),
        };
    }

    toText() {
        return this.content === null ? '' : this.content.toText();
    }

    static from(original: CellContent): CellContent {
        return new CellContent(original.editor_type, original.content);
    }

    static createNew() {
        return new CellContent(null, null);
    }

    static fromJSON(object: any) {
        if (object instanceof Object && object.hasOwnProperty('content')) {
            // new style
            const editor_type = object.editor_type;
            let content: CellContentInterface | null = null;
            if (object.editor_type === 'perception') {
                debug_log("...", object.content);
                content = PerceptionContent.fromJSON(object.content);
            } else if (object.editor_type === 'behavior') {
                debug_log("...", object.content);
                content = BehaviorLines.fromJSON(object.content);
            }
            return new CellContent(editor_type, content);
        } else {
            // old style
            let content: CellContentInterface | undefined;

            if (object.editor_type === 'perception') {
                if (object.raw_draft_content_state) {
                    content = PerceptionContent.fromJSON(object.raw_draft_content_state);
                } else {
                    content = PerceptionContent.createNew();
                }
            } else if (object.editor_type === 'behavior' || object.editor_type === 'action') {
                if (object.raw_behavior_lines) {
                    content = BehaviorLines.fromJSON(object.raw_behavior_lines);
                } else {
                    content = BehaviorLines.fromStr(CellContent.INITIAL_TEXT);
                }
            }

            debug_log("fromJSON", object);
            return new CellContent(object.editor_type, content);
        }

    }

    setFontName(font: string): CellContent {
        if (this.content === null) {
            throw new Error("content is null");
        }
        return new CellContent(this.editor_type, this.content.changeTextStyle('font', font));
    }

    setFontSize(size: string): CellContent {
        if (this.content === null) {
            throw new Error("content is null");
        }
        return new CellContent(this.editor_type, this.content.changeTextStyle('size', size));
    }

    setFontColor(color: string): CellContent {
        if (this.content === null) {
            throw new Error("content is null");
        }
        return new CellContent(this.editor_type, this.content.changeTextStyle('color', color));
    }

    toggleBold(): CellContent {
        if (this.content === null) {
            throw new Error("content is null");
        }
        return new CellContent(this.editor_type, this.content.toggleTextStyle('bold'));
    }

    setAlignment(align: AlignmentType): CellContent {
        if (this.content === null) {
            throw new Error("content is null");
        }
        return new CellContent(this.editor_type, this.content.changeTextStyle('align', align));
    }

    getTextStyle(): TextStyle {
        if (this.content === null) {
            return TextStyle.createDefault();
        }
        return this.content.getTextStyle();
    }

}
